import { ConversationMessage, IMessage } from "@/core/entities/conversation";
import { DocumentMimeType } from "@/core/enums/DocumentMimeType";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { AppConfig } from "@/infrastructure/services/config";

const dateAdapter = new DateAdapter();

export interface GroupedMessages {
  date: string;
  messages: ConversationMessage[];
}

const _appConfig = AppConfig.getInstance();

const TIME_ZONE = _appConfig.TIMEZONE;
const DATE_FORMAT = _appConfig.DEFAULT_DATE_FORMAT;
const TIME_FORMAT = _appConfig.DEFAULT_TIME_FORMAT;

export class ConversationUtils {
  static readonly getFlattenMessages = (groupedMessages: GroupedMessages[]): ConversationMessage[] => {
    return groupedMessages.flatMap(group => group.messages);
  };

  static readonly groupMessagesByDate = (
    existing: GroupedMessages[],
    messages: ConversationMessage[]
  ): GroupedMessages[] => {
    const existingMessages = this.getFlattenMessages(existing);
    const newMessages = [...messages];
    const allMessages = [...existingMessages];
    //NOSONAR
    // for (const eachMessage of newMessages) {
    //   const existingMessage = allMessages.find(m => m.requestUniqueKey == eachMessage.requestUniqueKey);
    //   if (existingMessage) {
    //     if (eachMessage.id) existingMessage.updateMessageId(eachMessage.id);
    //     if (eachMessage.attachments) existingMessage.updateAttachments(eachMessage.attachments);
    //   } else {
    //     allMessages.push(eachMessage);
    //   }
    // }

    for (const eachMessage of newMessages) {
      const existingMessage = allMessages.find(m => m.requestUniqueKey == eachMessage.requestUniqueKey);
      if (existingMessage) {
        if (eachMessage.id) existingMessage.updateMessageId(eachMessage.id);
        if (eachMessage.attachments) existingMessage.updateAttachments(eachMessage.attachments);
        if (eachMessage.isError !== undefined) existingMessage.updateIsError(eachMessage.isError);
      } else {
        allMessages.push(eachMessage);
      }
    }

    // sort messages by createdAt
    const sortedMessages = allMessages.toSorted(
      (a, b) => new Date(a.createdAt ?? "").getTime() - new Date(b.createdAt ?? "").getTime()
    );

    // group messages by date
    const groupedMessages = sortedMessages.reduce(
      (acc, message) => {
        const date = dateAdapter.format(message.createdAt ?? "", "YYYY-MM-DD");
        acc[date] = [...(acc[date] || []), message];
        return acc;
      },
      {} as Record<string, IMessage[]>
    );

    return Object.entries(groupedMessages).map(([date, messages]) => ({
      date,
      messages: messages as ConversationMessage[],
    }));
  };

  static readonly updateMessageFailedStatus = (messages: GroupedMessages[], requestUniqueKey: string) => {
    const flattenedMessages = this.getFlattenMessages(messages);
    const message = flattenedMessages.find(message => message.requestUniqueKey === requestUniqueKey);
    if (message) message.updateIsError(true);
    return this.groupMessagesByDate([], flattenedMessages);
  };

  // change date today, yesterday, else YYYY-MM-DD
  static formatDate(date: string, format: string = DATE_FORMAT) {
    const formattedDate = dateAdapter.format(date, format);
    const today = dateAdapter.format(new Date().toISOString(), format);
    const yesterday = dateAdapter.format(new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(), format);
    if (formattedDate === today) return "Today";
    if (formattedDate === yesterday) return "Yesterday";
    return formattedDate;
  }

  // format local date time to HH:mm
  static formatLocalDateTime(date: string, format: string = TIME_FORMAT) {
    return dateAdapter.formatInTimeZone(date, TIME_ZONE, format);
  }

  static readonly getFileType = (file: File): DocumentMimeType => {
    const isEdiFile = file.name?.toLowerCase().endsWith(".edi");
    return isEdiFile ? DocumentMimeType.EDI : (file.type.toLowerCase() as DocumentMimeType);
  };

  static readonly sanitizeFileName = (filename: string): string => {
    const sanitized = filename
      .replaceAll(/[<>:"/\\|?*]/g, "")
      .replaceAll(/\s+/g, "_")
      .replaceAll(/[^a-zA-Z0-9._-]/g, "")
      .replaceAll(/^\.+/g, "")
      .replaceAll(/\.{2,}/g, ".")
      .trim();

    return sanitized || "untitled";
  };
}
